import { createSlice, createAction, type PayloadAction, type Reducer } from '@reduxjs/toolkit';
import type {
  ISchedulerFilters,
  ISchedulerPanelState,
  SchedulerZoom,
  SchedulerPanelTab,
  TaskId,
} from '@interfaces/IScheduler';
import { clampZoomScale, ZOOM_STEP } from '@components/Pages/SchedulerPage/lib/timeScale';
import { writeStickyGroupsPref } from '@utils/stickyGroupsPref';
import { writeStickyLabelsPref } from '@utils/stickyLabelsPref';
import {
  initialSchedulerState,
  MAX_HISTORY,
  type IPendingDelete,
  type ISchedulerState,
  type IUndoEntry,
} from './scheduler.state';
import {
  bootstrap,
  executionRefreshed,
  setStale,
  setFrozenTasks,
  markDirty,
  planVersionAcknowledged,
  reconcileSaved,
  rebaseOnFresh,
} from './scheduler.docReducers';
import {
  moveTask,
  resizeTask,
  addTask,
  addGroup,
  deleteGroup,
  updateTask,
  taskPlanFieldsSet,
  deleteTask,
  applyTaskColors,
  splitTaskByLocations,
  addDependency,
  deleteDependency,
  updateDependency,
  toggleGroupCollapsed,
  toggleTaskCollapsed,
  setSubtreeCollapsed,
  revealTask,
  addMarker,
  updateMarker,
  deleteMarker,
} from './scheduler.planReducers';
import {
  taskCommitted,
  taskUncommitted,
  taskStatusSet,
  taskChecklistItemToggled,
  taskCommentPosted,
  taskLocationAdded,
  taskLocationRemoved,
  taskLocationsBulkToggled,
  taskLocationStatusChanged,
  areaStatusesObserved,
  taskProgressSet,
  taskStockClaimsSet,
} from './scheduler.executionReducers';
import { diffTree, replayDelta, REDO, UNDO, type IReplay } from './scheduler.delta';

export * from './scheduler.state';

const slice = createSlice({
  name: 'scheduler',
  initialState: initialSchedulerState,
  reducers: {
    bootstrap,
    executionRefreshed,
    setStale,
    setFrozenTasks,
    markDirty,
    planVersionAcknowledged,
    reconcileSaved,
    rebaseOnFresh,
    moveTask,
    resizeTask,
    addTask,
    addGroup,
    deleteGroup,
    updateTask,
    taskPlanFieldsSet,
    deleteTask,
    applyTaskColors,
    taskCommitted,
    taskUncommitted,
    taskStatusSet,
    taskChecklistItemToggled,
    taskCommentPosted,
    taskLocationAdded,
    taskLocationRemoved,
    taskLocationsBulkToggled,
    taskLocationStatusChanged,
    areaStatusesObserved,
    taskProgressSet,
    taskStockClaimsSet,
    splitTaskByLocations,
    addDependency,
    deleteDependency,
    updateDependency,
    toggleGroupCollapsed,
    toggleTaskCollapsed,
    setSubtreeCollapsed,
    revealTask,

    setZoom(state, action: PayloadAction<SchedulerZoom>) {
      state.ui.zoom = action.payload;
    },

    /** Geometric zoom factor (magnifies the whole grid). UI-only: not undoable,
     *  not synced — same treatment as day/week/month zoom. Clamped to range.
     *  Sets an absolute value (e.g. from a slider); steppers use zoomIn/Out. */
    setZoomScale(state, action: PayloadAction<number>) {
      state.ui.zoomScale = clampZoomScale(action.payload);
    },

    /** Geometric zoom steppers. No payload — the next value depends on current
     *  state, which the reducer already owns. Keeps the step + clamp in one
     *  place and lets callers use stable, payload-less dispatches. */
    zoomIn(state) {
      state.ui.zoomScale = clampZoomScale(state.ui.zoomScale + ZOOM_STEP);
    },
    zoomOut(state) {
      state.ui.zoomScale = clampZoomScale(state.ui.zoomScale - ZOOM_STEP);
    },
    zoomReset(state) {
      state.ui.zoomScale = 1;
    },

    /** Scheduler-wide milestone diamond colour. UI-only view setting (not
     *  undoable, not synced) — applies live to every milestone. */
    setMilestoneColor(state, action: PayloadAction<string>) {
      state.ui.milestoneColor = action.payload;
    },

    /** Set (or clear, with null) the live auto-colorize preview map. UI-only:
     *  the canvas overlays it; nothing is persisted until Apply. */
    setColorPreview(state, action: PayloadAction<Record<TaskId, string> | null>) {
      state.ui.colorPreview = action.payload;
    },

    /** Toggle the in-bar progress (PPC) band. UI-only view setting. */
    setShowBarProgress(state, action: PayloadAction<boolean>) {
      state.ui.showBarProgress = action.payload;
    },

    /** Toggle sticky group/summary headers while scrolling. UI-only view
     *  setting, persisted to localStorage so it survives a reload. */
    setStickyGroups(state, action: PayloadAction<boolean>) {
      state.ui.stickyGroups = action.payload;
      writeStickyGroupsPref(action.payload);
    },

    /** Toggle bar labels following the horizontal scroll. UI-only view
     *  setting, persisted to localStorage so it survives a reload. */
    setStickyLabels(state, action: PayloadAction<boolean>) {
      state.ui.stickyLabels = action.payload;
      writeStickyLabelsPref(action.payload);
    },

    setSelectedTask(state, action: PayloadAction<TaskId | null>) {
      state.ui.selectedTaskId = action.payload;
      // selecting a task clears any dependency selection (mutually exclusive)
      state.ui.selectedDependencyId = null;
    },

    setSelectedDependency(state, action: PayloadAction<string | null>) {
      state.ui.selectedDependencyId = action.payload;
      // selecting a dep clears any task selection (mutually exclusive)
      if (action.payload !== null) state.ui.selectedTaskId = null;
    },

    openPanel(state, action: PayloadAction<ISchedulerPanelState>) {
      state.ui.panelOpen = action.payload;
      state.ui.panelTab = action.payload.initialTab
        ?? (action.payload.mode === 'create' ? 'edit' : 'overview');
    },

    setPanelTab(state, action: PayloadAction<SchedulerPanelTab>) {
      state.ui.panelTab = action.payload;
    },

    closePanel(state) {
      state.ui.panelOpen = null;
      // The commit review lives in the drawer; closing the drawer ends it.
      state.ui.commitTaskId = null;
    },

    /** Open the commit REVIEW for a task — inside its drawer, which opens (or
     *  switches to the task) if it is not already showing it (2026-09-18 commit
     *  UX design, decision 3: the review is the drawer's body, not a modal). */
    openCommitDialog(state, action: PayloadAction<TaskId>) {
      state.ui.commitTaskId = action.payload;
      if (state.ui.panelOpen?.mode !== 'edit' || state.ui.panelOpen.taskId !== action.payload) {
        state.ui.panelOpen = { mode: 'edit', taskId: action.payload };
        state.ui.panelTab = 'overview';
      }
    },

    closeCommitDialog(state) {
      state.ui.commitTaskId = null;
    },

    /** Ask before a row is deleted. The confirmation dispatches the (undoable)
     *  `deleteTask` / `deleteGroup` itself, then `cancelDelete` closes it. */
    requestDelete(state, action: PayloadAction<IPendingDelete>) {
      state.ui.pendingDelete = action.payload;
    },

    cancelDelete(state) {
      state.ui.pendingDelete = null;
    },

    setCriticalPathVisible(state, action: PayloadAction<boolean>) {
      state.ui.showCriticalPath = action.payload;
    },

    setFilters(state, action: PayloadAction<ISchedulerFilters>) {
      state.ui.filters = action.payload;
    },

    setSearch(state, action: PayloadAction<string>) {
      state.ui.search = action.payload;
    },

    setViewRange(state, action: PayloadAction<ISchedulerState['ui']['viewRange']>) {
      state.ui.viewRange = action.payload;
    },

    /** Lift every narrowing of the view at once — status/group filters, the
     *  search and the date range. The "no items match" Clear, and a link whose
     *  target the current view hides. */
    clearViewFilters(state) {
      state.ui.filters = { status: [], groupIds: [] };
      state.ui.search = '';
      state.ui.viewRange = null;
    },

    setCanvasWidthPx(state, action: PayloadAction<number>) {
      state.ui.canvasWidthPx = action.payload;
    },

    clearError(state) {
      state.ui.lastError = null;
    },

    setClipboardTaskId(state, action: PayloadAction<TaskId | null>) {
      state.ui.clipboardTaskId = action.payload;
    },

    addMarker,
    updateMarker,
    deleteMarker,
  },
});

// ---------------------------------------------------------------------------
// Undo / redo — actions live outside the slice so the HOR can intercept them
// before the base reducer sees them.
// ---------------------------------------------------------------------------

export const undoAction = createAction('scheduler/undo');
export const redoAction = createAction('scheduler/redo');
export const clearHistoryAction = createAction('scheduler/clearHistory');
export const revertLastAction = createAction('scheduler/revertLast');

// Actions whose bySite mutations should be tracked in the undo history.
const UNDOABLE = new Set<string>([
  'scheduler/moveTask',
  'scheduler/resizeTask',
  'scheduler/addTask',
  'scheduler/updateTask',
  'scheduler/deleteTask',
  'scheduler/splitTaskByLocations',
  'scheduler/applyTaskColors',
  'scheduler/addGroup',
  'scheduler/deleteGroup',
  'scheduler/addDependency',
  'scheduler/deleteDependency',
  'scheduler/updateDependency',
  'scheduler/toggleGroupCollapsed',
  'scheduler/toggleTaskCollapsed',
  'scheduler/setSubtreeCollapsed',
  'scheduler/addMarker',
  'scheduler/updateMarker',
  'scheduler/deleteMarker',
  // Checklist authoring is an Edit-tab draft field now: it reaches the store
  // (and history) through `updateTask` on the drawer's Save.
  // Area ASSIGNMENT, profession and crew size are plan edits the drawer saves
  // as it makes them (2026-09-23 amendment) — each rides its own PUT, so, like
  // the status row actions below, undoing one locally would desync from it.
  // 'scheduler/taskLocationAdded',
  // 'scheduler/taskLocationRemoved',
  // 'scheduler/taskLocationsBulkToggled',
  // 'scheduler/taskPlanFieldsSet',
  // Area STATUS is Trusstor's, written through its own endpoint — not undoable.
  // 'scheduler/taskLocationStatusChanged',
  // Commit / uncommit / plan-status changes are immediate-write too — each
  // rides its own PUT, so undoing locally would silently desync from what was
  // already persisted (and, for a commit, from Trusstor).
  // 'scheduler/taskCommitted',
  // 'scheduler/taskUncommitted',
  // 'scheduler/taskStatusSet',
  // Excluded: comments and checklist ticks (journal-shaped writes).
  // 'scheduler/taskCommentPosted',
  // 'scheduler/taskChecklistItemToggled',
]);

// Actions whose effects are persisted to D-ORCA on save (mark the site dirty).
// D-ORCA now OWNS the schedule, so the rich PLAN editor fields — checklist
// DEFINITIONS (authoring), markers, notes/trade/workerCount (via updateTask) —
// are stored data and must surface as dirty so Save persists them. Left OUT:
// pure view state (collapse, never synced) AND the execution-facet immediate
// writes — comments and checklist ticks moved to their own granular endpoints
// (phase 2), so like progress/status/locations they persist instantly and never
// batch into Save.
const SYNCABLE = new Set<string>([
  'scheduler/moveTask',
  'scheduler/resizeTask',
  'scheduler/addTask',
  'scheduler/updateTask',
  'scheduler/deleteTask',
  'scheduler/splitTaskByLocations',
  'scheduler/applyTaskColors',
  'scheduler/addGroup',
  'scheduler/deleteGroup',
  'scheduler/addDependency',
  'scheduler/deleteDependency',
  'scheduler/updateDependency',
  // Checklist DEFINITION authoring is a PLAN edit that rides `updateTask` (the
  // drawer draft), which marks dirty above. A committed task's amendment has
  // its own immediate route (task-checklist design, decision 5).
  // Checklist ticks and comments moved to their own granular endpoints (phase 2);
  // they persist immediately and no longer mark the site dirty / batch into Save.
  // 'scheduler/taskChecklistItemToggled',
  // 'scheduler/taskCommentPosted',
  // Progress (Overview slider) is persisted immediately by the progress section
  // (a dedicated PUT), so it no longer marks the site dirty / batches into Save.
  // 'scheduler/taskProgressSet',
  // Area ASSIGNMENT, profession and crew size are auto-saved by the drawer
  // (2026-09-23 amendment) — they never batch into Save. A task not saved yet
  // is the exception, and it is already dirty from being added.
  // 'scheduler/taskLocationAdded',
  // 'scheduler/taskLocationRemoved',
  // 'scheduler/taskLocationsBulkToggled',
  // 'scheduler/taskPlanFieldsSet',
  // Area STATUS goes straight through to Trusstor; commit / uncommit / plan
  // status each ride their own PUT — none of them batch into Save.
  // 'scheduler/taskLocationStatusChanged',
  // 'scheduler/taskCommitted',
  // 'scheduler/taskUncommitted',
  // 'scheduler/taskStatusSet',
  'scheduler/addMarker',
  'scheduler/updateMarker',
  'scheduler/deleteMarker',
]);

/** The siteId carried by every SYNCABLE action's payload (and `bootstrap`'s data). */
function actionSiteId(action: unknown): string | null {
  const payload = (action as { payload?: { siteId?: unknown } }).payload;
  return typeof payload?.siteId === 'string' ? payload.siteId : null;
}

// Every action that mutates a site's DATA — all frozen while the site is stale,
// because editing a stale base only manufactures conflicts (and journal /
// immediate-write entries would be lost on the rebase). Three overlapping sets
// classify data actions by concern:
//   • UNDOABLE  — recorded in undo/redo history (structural edits).
//   • SYNCABLE  — mark the site dirty and batch into Save (structural edits +
//                 journal-shaped writes: comments, checklist authoring/ticks).
//   • FREEZABLE — ANY data mutation, the WIDEST set. It must also cover the
//                 immediate-write actions (status lifecycle, progress, per-task
//                 locations) that are deliberately in NEITHER of the other two:
//                 they persist instantly via their own targeted PUTs, so they
//                 skip undo/Save — but on a stale base they would still mutate
//                 the store and round-trip, clobbering a concurrent editor.
// Derive from UNDOABLE, then explicitly add every write that isn't already in it.
const FREEZABLE = new Set<string>([
  ...UNDOABLE,
  // Execution-facet immediate writes — in NEITHER UNDOABLE nor SYNCABLE (they
  // persist instantly via their own granular endpoints), but they MUST still
  // freeze on a stale base, or an edit on a soon-to-be-rebased base would
  // round-trip and clobber a concurrent editor.
  'scheduler/taskCommentPosted',
  'scheduler/taskChecklistItemToggled',
  'scheduler/taskCommitted',
  'scheduler/taskUncommitted',
  'scheduler/taskStatusSet',
  'scheduler/taskProgressSet',
  'scheduler/taskLocationStatusChanged',
  // Stock claims (ruling 9, 2026-09-09) are an execution fact like the areas
  // above: the drawer commits the whole set through its own per-task PUT, so
  // the action is frozen on a stale base but in neither UNDOABLE nor SYNCABLE.
  'scheduler/taskStockClaimsSet',
  // The drawer's auto-saved plan fields (2026-09-23 amendment), for the same reason.
  'scheduler/taskLocationAdded',
  'scheduler/taskLocationRemoved',
  'scheduler/taskLocationsBulkToggled',
  'scheduler/taskPlanFieldsSet',
]);

// Actions that replace a site's working copy wholesale — a load, a schedule
// switch, a Save's server doc, a rebase. History belongs to the copy it was
// recorded on, so it ends with that copy: an undo can never reach into another
// schedule's (or a superseded) doc (QA F-44).
const DOC_REPLACING = new Set<string>([
  'scheduler/bootstrap',
  'scheduler/reconcileSaved',
  'scheduler/rebaseOnFresh',
]);

const EMPTY_HISTORY: ISchedulerState['history'] = { past: [], future: [] };

function withDirty(state: ISchedulerState, siteId: string | null): ISchedulerState {
  if (!siteId || state.dirtyBySite[siteId]) return state;
  return { ...state, dirtyBySite: { ...state.dirtyBySite, [siteId]: true } };
}

/** Replay one history entry on the plan facet. */
function replayEntry(state: ISchedulerState, entry: IUndoEntry, replay: IReplay): Pick<ISchedulerState, 'bySite'> {
  return { bySite: replayDelta(state.bySite, entry.plan, replay) as ISchedulerState['bySite'] };
}

const baseReducer = slice.reducer;

/**
 * Higher-order reducer that wraps the slice reducer to add undo/redo.
 *
 * For undoable actions we run the base reducer, then record the DELTA from the
 * old to the new plan facet (`bySite`) by key and entity id
 * (`scheduler.delta.ts`). Every undoable action is a plan edit; the execution
 * facet is written straight through and never enters history. The entry goes onto `history.past`, the
 * redo stack is cleared, and the stack is capped at MAX_HISTORY.
 *
 * Undo / redo replay an entry GUARDED: a field goes back (or forward) only
 * while it still holds the value the entry left there. A write saved instantly
 * since — status, areas, profession, crew size, progress, all outside history —
 * is a newer value and survives (QA F-45). Revert-last (an optimistic write the
 * server refused) is a silent undo.
 *
 * History ends whenever the working copy is replaced (DOC_REPLACING).
 *
 * UI-only actions (zoom, panel, selection…) pass straight through with no
 * history entry.
 */
export const schedulerReducer: Reducer<ISchedulerState> = (
  state = initialSchedulerState,
  action,
) => {
  // --- stale freeze: data edits on a stale site are dropped wholesale ---
  if (FREEZABLE.has(action.type)) {
    const siteId = actionSiteId(action);
    if (siteId && state.staleBySite[siteId]) return state;
  }

  // --- a new working copy: the old one's history goes with it ---
  if (DOC_REPLACING.has(action.type)) {
    return { ...baseReducer(state, action), history: EMPTY_HISTORY };
  }

  // --- undo ---
  if (undoAction.match(action)) {
    const entry = state.history.past.at(-1);
    if (!entry || (entry.siteId && state.staleBySite[entry.siteId])) return state;
    // No dirty bookkeeping: an entry only exists after an edit that already
    // flagged the site, and the flag only clears with a new working copy —
    // which ends history.
    return {
      ...state,
      ...replayEntry(state, entry, UNDO),
      history: {
        past: state.history.past.slice(0, -1),
        future: [entry, ...state.history.future],
      },
    };
  }

  // --- redo ---
  if (redoAction.match(action)) {
    const entry = state.history.future[0];
    if (!entry || (entry.siteId && state.staleBySite[entry.siteId])) return state;
    return {
      ...state,
      ...replayEntry(state, entry, REDO),
      history: {
        past: [...state.history.past, entry],
        future: state.history.future.slice(1),
      },
    };
  }

  // --- clear history ---
  if (clearHistoryAction.match(action)) {
    return { ...state, history: EMPTY_HISTORY };
  }

  // --- revert last: silent rollback — pops past, does NOT push to future ---
  if (revertLastAction.match(action)) {
    const entry = state.history.past.at(-1);
    if (!entry) return state;
    return {
      ...state,
      ...replayEntry(state, entry, UNDO),
      history: {
        past: state.history.past.slice(0, -1), // pop, do NOT push to future
        future: state.history.future,          // unchanged
      },
    };
  }

  // --- undoable actions: run base reducer, record what changed ---
  if (UNDOABLE.has(action.type)) {
    const nextState = baseReducer(state, action);
    const plan = diffTree(state.bySite, nextState.bySite);
    // No actual change (e.g. addDependency rejected a cycle) — skip history.
    if (!plan) return nextState;

    const siteId = actionSiteId(action);
    const entry: IUndoEntry = { plan, actionType: action.type, ...(siteId ? { siteId } : {}) };
    const newPast = [...state.history.past, entry].slice(-MAX_HISTORY);

    return withDirty(
      {
        ...nextState,
        history: {
          past: newPast,
          future: [], // any new action clears the redo stack
        },
      },
      SYNCABLE.has(action.type) ? siteId : null,
    );
  }

  // --- syncable-but-not-undoable writes ---
  // Journal-shaped writes that mark dirty without an undo entry. Currently every
  // SYNCABLE action is also UNDOABLE (comments + checklist ticks moved to the
  // execution facet's immediate-write endpoints in phase 2), so this branch is
  // dormant; it is kept so a future SYNCABLE-only action gets the dirty flag.
  if (SYNCABLE.has(action.type) && !UNDOABLE.has(action.type)) {
    const nextState = baseReducer(state, action);
    if (nextState.bySite === state.bySite) return nextState;
    return withDirty(nextState, actionSiteId(action));
  }

  // --- everything else: pass through ---
  return baseReducer(state, action);
};

export const schedulerActions = slice.actions;

export type { TaskStatus } from '@interfaces/IScheduler';
